import React, { useEffect, useMemo, useState } from 'react';
import { Button, Form, Modal, Spinner } from 'react-bootstrap';
import Select from 'react-select';
import { ABORT_ERROR, makeRESTApiRequest } from '../../../helpers/api';
import { ENDPOINTS } from '../../../helpers/constants';
import CircularProgressBar from '../../circular-progress';
import { selectStyles } from '../../../helpers/global';
import moment from 'moment';

const AddEditTimeOffLogModal = ({
  show,
  onHide,
  showProgress,
  onSubmit,
  edit,
  teamMembers = [],
  isTeamMembersLoading = false
}) => {
  const [formData, setFormData] = useState({
    userId: '',
    startDate: '',
    endDate: '',
    notes: ''
  });
  const [errors, setErrors] = useState({});
  const [workDaysCount, setWorkDaysCount] = useState(0);
  const [workDaysLoading, setWorkDaysLoading] = useState(false);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    if (errors[field]) {
      setErrors(prev => {
        const next = { ...prev };
        delete next[field];
        return next;
      });
    }
  };

  const userOptions = useMemo(
    () =>
      teamMembers.map(user => ({
        label: user.name,
        value: user._id
      })),
    [teamMembers]
  );

  const selectedUser = useMemo(() => userOptions.find(option => option.value === formData.userId) || null, [
    formData.userId,
    userOptions
  ]);

  useEffect(() => {
    if (!show) {
      setWorkDaysCount(0);
      setWorkDaysLoading(false);
      return;
    }

    if (!formData.startDate) {
      setWorkDaysCount(0);
      setWorkDaysLoading(false);
      return;
    }

    if (formData.endDate && formData.endDate < formData.startDate) {
      setWorkDaysCount(0);
      setWorkDaysLoading(false);
      return;
    }

    const controller = new AbortController();
    setWorkDaysLoading(true);

    const requestBody = {
      startDate: formData.startDate,
      ...(formData.endDate ? { endDate: formData.endDate } : {})
    };

    (async () => {
      const { response, error } = await makeRESTApiRequest({
        endpoint: ENDPOINTS.TIME_OFF_LOGS_WORK_DAYS,
        requestBody,
        signal: controller.signal
      });

      if (error === ABORT_ERROR) {
        return;
      }

      setWorkDaysLoading(false);

      if (error) {
        setWorkDaysCount(0);
        return;
      }

      const n = response?.workDays;
      setWorkDaysCount(typeof n === 'number' && !Number.isNaN(n) ? n : 0);
    })();

    return () => controller.abort();
  }, [show, formData.startDate, formData.endDate]);

  const validateForm = () => {
    const newErrors = {};
    if (!formData.userId) {
      newErrors.userId = 'User is required';
    }
    if (!formData.startDate) {
      newErrors.startDate = 'Start date is required';
    }
    if (formData.startDate && formData.endDate && formData.endDate < formData.startDate) {
      newErrors.endDate = 'End date must be on or after start date';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = e => {
    e.preventDefault();
    if (!validateForm()) {
      return;
    }
    onSubmit(formData);
  };

  useEffect(() => {
    if (!show) {
      setErrors({});
      return;
    }
    if (edit?._id) {
      setFormData({
        userId: edit.userId?._id || edit.userId || '',
        startDate: edit.startDate ? moment(edit.startDate).format('YYYY-MM-DD') : '',
        endDate: edit.endDate ? moment(edit.endDate).format('YYYY-MM-DD') : '',
        notes: edit.notes || ''
      });
      setErrors({});
      return;
    }

    setFormData({
      userId: '',
      startDate: '',
      endDate: '',
      notes: ''
    });
    setErrors({});
  }, [edit, show]);

  return (
    <Modal show={show} onHide={onHide} centered backdrop="static">
      <Modal.Header closeButton>
        <Modal.Title>{edit?._id ? 'Edit Time Off' : 'Add Time Off'}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form onSubmit={handleSubmit}>
          <Form.Group>
            <Form.Label>
              User <span className="text-danger">*</span>
            </Form.Label>
            <Select
              placeholder={isTeamMembersLoading ? 'Loading users...' : 'Select user'}
              isDisabled={isTeamMembersLoading || showProgress}
              options={userOptions}
              value={selectedUser}
              onChange={option => handleInputChange('userId', option?.value || '')}
              styles={selectStyles}
              isSearchable
              className={`basic-single select-organization${errors.userId ? ' is-invalid' : ''}`}
              classNamePrefix="select"
            />
            {errors.userId && <div className="text-danger small mt-1">{errors.userId}</div>}
          </Form.Group>
          <Form.Group>
            <Form.Label>
              Start Date <span className="text-danger">*</span>
            </Form.Label>
            <Form.Control
              type="date"
              value={formData.startDate}
              className={errors.startDate ? 'is-invalid' : ''}
              onChange={e => handleInputChange('startDate', e.target.value)}
            />
            {errors.startDate && <div className="text-danger small mt-1">{errors.startDate}</div>}
          </Form.Group>
          <Form.Group>
            <Form.Label>End Date</Form.Label>
            <Form.Control
              type="date"
              value={formData.endDate}
              className={errors.endDate ? 'is-invalid' : ''}
              onChange={e => handleInputChange('endDate', e.target.value)}
            />
            {errors.endDate && <div className="text-danger small mt-1">{errors.endDate}</div>}
          </Form.Group>
          <div className="d-flex align-items-center mb-2">
            <span className="midFont font-weight-bold mr-1">Total Work Days:</span>
            <span className="text-dark midFont font-weight-bold">
              {workDaysLoading ? <CircularProgressBar className="ml-1" /> : workDaysCount}
            </span>
          </div>
          <Form.Group>
            <Form.Label>Notes</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              placeholder="Optional notes about this time off"
              value={formData.notes}
              onChange={e => handleInputChange('notes', e.target.value)}
            />
          </Form.Group>
          <div className="d-flex justify-content-end">
            <Button
              variant="light"
              className="border d-flex align-items-center justify-content-center px-2 mr-2"
              title="More"
              disabled={showProgress}
              onClick={onHide}
            >
              Cancel
            </Button>
            <Button
              variant="success"
              type="submit"
              className="d-inline-flex align-items-center justify-content-center"
              disabled={showProgress || (formData.startDate && workDaysLoading)}
            >
              {showProgress && <Spinner size="sm" animation="border" className="mr-1" />}
              {edit?._id ? 'Update' : 'Submit'}
            </Button>
          </div>
        </Form>
      </Modal.Body>
    </Modal>
  );
};

export default AddEditTimeOffLogModal;
